import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  FormQuestionConfigDto,
  FormQuestionDto,
  FormSectionDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_FORM_SECTIONS_CONFIG } from "../domain";

import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";
import { formsSectionsConfigApi } from "@api/services";

export const ACTION_ADD_FORM_QUESTION_CONFIG = "ADD_FORM_QUESTION_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type AddQuestionToSectionProps = {
  sectionId: string;
  questionIds: FormQuestionConfigDto[];
};

const addQuestionsToSection = async (
  props: AddQuestionToSectionProps,
): Promise<FormQuestionDto[]> => {
  const response =
    await formsSectionsConfigApi.formSectionsConfigControllerAddQuestionsToForm(
      props.sectionId,
      props.questionIds,
    );

  return response.data;
};

export const useAddQuestionsToSection = (
  user: UserProfileDto | null,
  formId: string,
  sectionId: string,
): {
  addQuestionsToSection: UseMutateFunction<
    FormQuestionDto[],
    Error,
    AddQuestionToSectionProps,
    unknown
  >;
  addQuestionsToSectionAsync: (
    props: AddQuestionToSectionProps,
  ) => Promise<FormQuestionDto[]>;
  addQuestionsToSectionEnabled: boolean;
  isSuccessAddQuestionsToSection: boolean;
  isPendingAddQuestionsToSection: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SECTIONS_CONFIG, ACTION_ADD_FORM_QUESTION_CONFIG],
    mutationFn: enabled ? addQuestionsToSection : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((section) => ({
          ...section,
          formQuestions:
            section.id === sectionId
              ? section.formQuestions
                ? [...section.formQuestions, ...data]
                : [...data]
              : section.formQuestions,
        }));
      });
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        enqueueSnackbar(
          t(
            "forms.snackbar.questionCodeAlreadyExistsInSection",
            "Question with this code already exists in the section",
          ),
          { variant: "error" },
        );
      }
    },
  });

  return {
    addQuestionsToSection: mutate,
    addQuestionsToSectionAsync: mutateAsync,
    addQuestionsToSectionEnabled: enabled,
    isSuccessAddQuestionsToSection: isSuccess,
    isPendingAddQuestionsToSection: isPending,
  };
};
